import React from 'react';
import { Field, Form, Formik } from 'formik';
import * as yup from 'yup';

import FormField from '@/components/forms/form-field';
import ManyAutocompleteField from '@/components/forms/many-autocomplete';
import OneWithImagesAutocompleteField from '@/components/forms/one-with-image-autocomplete';
import { FORMS } from '@/static/common.js';
import { arrangeData } from '@/middlewares/api';
import { withFormikFieldAdapter as formikFieldAdapter } from '@/components/forms/formik-adapter';
import { autocompleteString } from '@/utils/forms';

// YouTube URL regex (matches backend rule)
const YOUTUBE_LINK_REGEX = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=|\?v=)([^#\&\?]*).*/;

const FormFieldFormik = formikFieldAdapter(FormField);
const ManyAutocompleteFieldFormik = formikFieldAdapter(ManyAutocompleteField);
const OneWithImagesAutocompleteFieldFormik = formikFieldAdapter(OneWithImagesAutocompleteField);

const validationSchema = yup.object({
    sampled_name: yup.string().required('Name of sampled song is required').max(255, 'Must be at most 255 characters'),
    sampled_link: yup
        .string()
        .required('YouTube link is required')
        .matches(YOUTUBE_LINK_REGEX, 'Please enter a valid YouTube link'),
    sampled_labels: yup.array().required('Record label is required').min(1, 'Select at least one label'),
    sampled_writers: yup.array().required('Writers are required').min(1, 'Select at least one writer'),
    sampled_publishers: yup.array().required('Publisher(s) are required').min(1, 'Select at least one publisher'),
    sampled_artist_name: autocompleteString(yup, 'Sampled artist name is required').max(255, 'Must be at most 255 characters'),
});

const initialValuesShape = {
    sampled_name: '',
    sampled_link: '',
    sampled_labels: [],
    sampled_writers: [],
    sampled_publishers: [],
    sampled_artist_name: '',
};

function UploadSampleStep3({ nextStep, prevStep, initialValues = {}, bgImg }) {
    const values = {
        ...initialValuesShape,
        ...initialValues,
    };

    const stepSubmit = (data) => {
        const payload = { ...data };
        const formData = arrangeData(payload, ['sampled_artist_name'], ['sampled_labels', 'sampled_writers', 'sampled_publishers']);
        nextStep(formData, data);
    };

    const goBack = (currentValues) => {
        prevStep(currentValues);
    };

    return (
        <section className="upload-form__sec" style={bgImg}>
            <div className="content">
                <div className="upload-form__top">
                    <h2 className="head-shadow">CLEAR SAMPLES</h2>
                    <h3 className="head">Step 3 of 4: THE SAMPLED SONG</h3>
                </div>
                <div className="form-step__body">
                    <Formik
                        initialValues={values}
                        validationSchema={validationSchema}
                        enableReinitialize
                        onSubmit={(data) => stepSubmit(data)}
                    >
                        {({ values: currentValues, isSubmitting }) => (
                            <Form className="form-layout" noValidate>
                                <div className="form-row">
                                    <div className="form-col_2">
                                        <Field
                                            name="sampled_name"
                                            component={FormFieldFormik}
                                            required={true}
                                            type="text"
                                            placeholder=""
                                            label="Name of Sampled Song"
                                        />
                                    </div>
                                    <div className="form-col_2">
                                        <Field
                                            name="sampled_link"
                                            component={FormFieldFormik}
                                            required={true}
                                            type="text"
                                            placeholder="YouTube Link"
                                            label="YouTube Link to Sampled Song"
                                        />
                                    </div>
                                </div>
                                <div className="form-row">
                                    <div className="form-col_2">
                                        <Field
                                            name="sampled_labels"
                                            component={ManyAutocompleteFieldFormik}
                                            required={true}
                                            url="clear/label/search"
                                            placeholder={FORMS.PLACEHOLDER_TBD}
                                            label="Record Label of Sampled Song [Enter TBD if Unknown]"
                                        />
                                    </div>
                                    <div className="form-col_2">
                                        <Field
                                            name="sampled_writers"
                                            component={ManyAutocompleteFieldFormik}
                                            required={true}
                                            url="clear/writer/search"
                                            placeholder={FORMS.PLACEHOLDER_TBD}
                                            label="Writers of the Sampled Song [Enter TBD if Unknown]"
                                        />
                                    </div>
                                </div>
                                <div className="form-row">
                                    <div className="form-col_2">
                                        <Field
                                            name="sampled_publishers"
                                            component={ManyAutocompleteFieldFormik}
                                            required={true}
                                            url="clear/publisher/search"
                                            placeholder={FORMS.PLACEHOLDER_TBD}
                                            label="Publisher(s) of Sampled Song [Enter TBD if Unknown]"
                                        />
                                    </div>
                                    <div className="form-col_2">
                                        <Field
                                            name="sampled_artist_name"
                                            component={OneWithImagesAutocompleteFieldFormik}
                                            url="artist/search"
                                            required={true}
                                            placeholder="Set Artist First and Last Name"
                                            label="Sampled Artist Name [Enter TBD if Unknown]"
                                        />
                                    </div>
                                </div>
                                <div className="submit-row submit-row_txt">
                                    <button
                                        className="btn btn-second"
                                        onClick={() => goBack(currentValues)}
                                        type="button"
                                    >
                                        Back
                                    </button>
                                    <button
                                        className="btn-red-primary"
                                        disabled={isSubmitting}
                                        type="submit"
                                    >
                                        Next
                                    </button>
                                </div>
                            </Form>
                        )}
                    </Formik>
                </div>
            </div>
        </section>
    );
}

export default UploadSampleStep3;
